import { useCallback, useMemo } from 'react';
import {
  imageResultSelectorFam,
  searchPageNoAtom,
  searchPageSizeAtom,
} from 'packages/omnitag/state';
import {
  OT_PAGE_NO_PARAM_NAME,
  OT_PAGE_SIZE_PARAM_NAME,
} from 'packages/omnitag/constants';
import { useHistory, useLocation } from 'react-router-dom';
import FilterResultZoomedImage from 'packages/lib/common-ui/log-miner-legacy-ui/FilterResultZoomedImage';
import { useQuery } from 'packages/lib/utils/hooks/use-query';
import { REQ_STATUS } from 'packages/lib/common-types/types';
import { replaceHistorySearch } from 'packages/lib/utils/url';
import * as H from 'history';
import { getBBoxStrFromImageMetadata } from 'packages/omnitag/utils';
import { OmnitagResultItem } from 'packages/lib/api/omnitag/contract/omnitag-search';
import { paginatedFilteredResultsSelector } from 'packages/omnitag/state/text-search';
import { useAtomValue, useSetAtom } from 'jotai';
import { toggleSelectedResultTransaction } from 'packages/omnitag/state/selected-results';
import { selectedResultsAtom } from 'packages/omnitag/state/selected-results';
import { OTMetadataPanel } from 'packages/polaris/tool/metadata-panel';

const useImageMetadata = (
  resultsCurPage: OmnitagResultItem[] | null,
  log_name: string | null,
  timestamp: string | null,
  camera_channel: string | null,
  bboxStr: string | null
): OmnitagResultItem | null | undefined => {
  const result = useMemo(() => {
    if (resultsCurPage) {
      return resultsCurPage.find(
        (item) =>
          item.channel === camera_channel &&
          item.timestamp.toString() === timestamp &&
          item.log_name === log_name &&
          getBBoxStrFromImageMetadata(item) === bboxStr
      );
    }
    return null;
  }, [resultsCurPage, camera_channel, log_name, timestamp, bboxStr]);

  return result;
};

// Params pulled from the URL, so they are different from the similar values as pulled from the API
interface OTImageMetaData {
  log_name: string;
  timestamp: string;
  camera_channel: string;
  bboxStr: string; // "l1877t619w938h618"
}

// NOTE: react-router-dom's useLocation() does not seem to play nice with recoil-sync for syncing recoil atoms to the URL.
// useLocation() has an empty search field when recoil-sync puts the params there. As such, we read the pageNo/pageSize atoms directly,
// rather than relying on location.search to contain them.
export function mergeImageQueryURLParams(
  location: H.Location<H.LocationState>,
  pageNo: string,
  pageSize: string,
  result: OmnitagResultItem
): URLSearchParams {
  const mergedParams = new URLSearchParams(location.search);

  mergedParams.set(OT_PAGE_NO_PARAM_NAME, pageNo);
  mergedParams.set(OT_PAGE_SIZE_PARAM_NAME, pageSize);
  mergedParams.set('logName', result.log_name);
  mergedParams.set('timestamp', result.timestamp.toString());
  mergedParams.set('camera_channel', result.channel);
  mergedParams.set('bbox', getBBoxStrFromImageMetadata(result));

  return mergedParams;
}

export const ZoomedImagePreview = (): JSX.Element => {
  const pageSize = useAtomValue(searchPageSizeAtom);
  const pageNo = useAtomValue(searchPageNoAtom);
  const params = useQuery();
  const history = useHistory();
  const location = useLocation();

  // Params added (elsewhere) when showing the zoomed image and details
  const log_name = params.get('logName');
  const timestamp = params.get('timestamp');
  const camera_channel = params.get('camera_channel');
  const bboxStr = params.get('bbox');

  const results = useAtomValue(paginatedFilteredResultsSelector);

  const curImageSrcFromURL = useAtomValue(
    imageResultSelectorFam({
      logName: log_name ?? '',
      timestamp: parseInt(timestamp ?? '0'),
      channel: camera_channel ?? '',
    })
  );

  const resultsCurPage: OmnitagResultItem[] | null =
    results &&
    !results.isValidating &&
    results.data &&
    Array.isArray(results.data)
      ? results.data
      : null;

  // Create a record contaning the log_name, timestamp, and camera_channel
  const curImageMetadataFromURL = useMemo(() => {
    let imageMetadata: OTImageMetaData | null = null;
    if (log_name && timestamp && camera_channel) {
      imageMetadata = {
        log_name,
        timestamp,
        camera_channel,
        bboxStr: bboxStr ?? '',
      };
    }
    return imageMetadata;
  }, [log_name, timestamp, camera_channel, bboxStr]);

  // Set the curImageIndex based on what's in the URL
  const curImageIndex = useMemo(() => {
    return curImageMetadataFromURL && resultsCurPage
      ? resultsCurPage.findIndex(
          (item) =>
            item.log_name === curImageMetadataFromURL.log_name &&
            item.timestamp.toString() === curImageMetadataFromURL.timestamp &&
            item.channel === curImageMetadataFromURL.camera_channel &&
            getBBoxStrFromImageMetadata(item) ===
              curImageMetadataFromURL.bboxStr
        )
      : -1;
  }, [resultsCurPage, curImageMetadataFromURL]);

  // Get metadata based on image index
  const curImageMetadata = useMemo((): OmnitagResultItem | null => {
    if (resultsCurPage && curImageIndex !== -1) {
      return resultsCurPage[curImageIndex];
    }
    return null;
  }, [curImageIndex, resultsCurPage]);

  // Since the annotation data isn't in the URL, we have to look it up to get the bounding_box[]
  const result = useImageMetadata(
    resultsCurPage,
    log_name,
    timestamp,
    camera_channel,
    bboxStr
  );

  const selectedSet = useAtomValue(selectedResultsAtom);
  const isSelected = result && selectedSet.has(result);
  const toggleSelectedResult = useSetAtom(toggleSelectedResultTransaction);

  const closeSidepanel = useCallback((): void => {
    const baseSearch = new URLSearchParams(location.search);
    baseSearch.delete('logName');
    baseSearch.delete('timestamp');
    baseSearch.delete('camera_channel');
    baseSearch.delete('bbox');
    replaceHistorySearch(history, location, baseSearch.toString());
  }, [location, history]);

  const toggleSelected = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>): void => {
      e.stopPropagation();
      toggleSelectedResult(result);
    },
    [result, toggleSelectedResult]
  );

  // Calculate URL for the Prev button
  const prevImageURLSearchStr = useMemo(() => {
    if (resultsCurPage && curImageIndex !== -1) {
      const prevImageMetadata = resultsCurPage[curImageIndex - 1];
      return curImageIndex - 1 >= 0
        ? '?' +
            mergeImageQueryURLParams(
              location,
              pageNo.toString(),
              pageSize.toString(),
              prevImageMetadata
            ).toString()
        : '';
    }
    return '';
  }, [curImageIndex, resultsCurPage, location, pageNo, pageSize]);

  // Calculate URL for the Prev button
  const nextImageURLSearchStr = useMemo(() => {
    if (resultsCurPage && curImageIndex !== -1) {
      const nextImageMetadata = resultsCurPage[curImageIndex + 1];
      return curImageIndex + 1 < resultsCurPage.length
        ? '?' +
            mergeImageQueryURLParams(
              location,
              pageNo.toString(),
              pageSize.toString(),
              nextImageMetadata
            ).toString()
        : '';
    }
    return '';
  }, [curImageIndex, resultsCurPage, location, pageNo, pageSize]);

  const metadataSidePanel: JSX.Element | null =
    log_name && timestamp && camera_channel && curImageMetadata ? (
      <OTMetadataPanel metadata={curImageMetadata} onClose={closeSidepanel} />
    ) : null;

  const panelHeader = <div style={{ display: 'grid' }}>Image Details</div>;

  return (
    <>
      {curImageMetadata && (
        <FilterResultZoomedImage
          panelHeader={panelHeader}
          imageSrc={curImageSrcFromURL}
          logName={log_name}
          cameraChannel={camera_channel}
          imageStatus={REQ_STATUS.success}
          metadataComponent={metadataSidePanel}
          annotationsRed={result?.bounding_box ?? undefined}
          annotationsBlue={result?.track_box_2d ?? undefined}
          isOpen={!!result}
          prevImageURLSearchStr={prevImageURLSearchStr}
          nextImageURLSearchStr={nextImageURLSearchStr}
          closeSidepanel={closeSidepanel}
          selected={!!isSelected}
          onSelect={toggleSelected}
        />
      )}
    </>
  );
};
